// What an out-of-buffer Go to Definition target IS: its shape, its identity
// (the `jamsql-nav:` URI Monaco carries around) and its one-line description.
// The registry that backs each URI with a model lives in
// `definitionNavRegistry.ts`; the navigation itself in
// `definitionNavigationOpener.ts`.

import type { QualifiedName } from '../types';
import type { ColumnNavigationTarget } from './columnNavigation';
import type { NavigationTarget } from './externalNavigation';
import type { RoutineNavigationTarget } from './routineNavigation';
import type { RoutinePickerCandidate, RoutinePickerContext } from './routinePickerStore';

export const DEFINITION_NAV_SCHEME = 'jamsql-nav';

/** One out-of-buffer navigation. `via` doubles as the analytics `via` value of
 *  `feature:navfeatures_goto_definition`. */
export type DefinitionNavTarget =
  | { readonly via: 'object-explorer'; readonly target: NavigationTarget }
  | { readonly via: 'column-target'; readonly target: ColumnNavigationTarget }
  | { readonly via: 'routine-target'; readonly target: RoutineNavigationTarget }
  | {
      readonly via: 'routine-picker';
      readonly candidates: readonly RoutinePickerCandidate[];
      readonly context: RoutinePickerContext;
    };

export function isDefinitionNavScheme(scheme: string | undefined): boolean {
  return scheme === DEFINITION_NAV_SCHEME;
}

/** `jamsql-nav:/<connection>/<database>/<kind>/<schema>/<name>.sql?target=<key>`.
 *
 *  The PATH is a label only: the Peek Definition tree shows its basename and
 *  directory, and its `.sql` suffix makes the hover card render the
 *  description as SQL. It may be ambiguous (`dbo.[a.b].c` and `dbo.a.[b.c]`
 *  both read `a.b.c`). The QUERY carries the identity — every field the
 *  navigation consumes, JSON-encoded as a tuple — so two targets share a URI
 *  (and so a model and a registry entry) only when they navigate to the same
 *  place. Monaco's labels never show the query. No authority: Monaco
 *  lowercases it on re-serialisation. */
export function buildDefinitionNavUri(nav: DefinitionNavTarget): string {
  const segments = labelSegments(nav).map((s) => encodeURIComponent(s && s.length > 0 ? s : '_'));
  return `${DEFINITION_NAV_SCHEME}:/${segments.join('/')}.sql?target=${encodeURIComponent(definitionNavKey(nav))}`;
}

/** Unambiguous identity of a navigation: a JSON tuple of every field the
 *  navigation reads, with `null` for an absent component (so an absent schema
 *  can never equal a schema literally named `_` or `null`). */
export function definitionNavKey(nav: DefinitionNavTarget): string {
  switch (nav.via) {
    case 'object-explorer': {
      const t = nav.target;
      return JSON.stringify([nav.via, t.connectionId, t.engineId, t.database ?? null, t.objectKind, ...nameKey(t.qualified)]);
    }
    case 'column-target': {
      const t = nav.target;
      return JSON.stringify([
        nav.via, t.connectionId, t.engineId, t.database ?? null, t.relationKind,
        ...nameKey(t.relation), ...nameKey(t.column),
      ]);
    }
    case 'routine-target': {
      const t = nav.target;
      return JSON.stringify([
        nav.via, t.connectionId, t.engineId, t.database ?? null, t.routineKind, t.objectId ?? null,
        ...nameKey(t.qualified),
      ]);
    }
    case 'routine-picker': {
      const c = nav.context;
      // The whole candidate list is the identity: a picker only ever collides
      // with a picker offering exactly the same routines.
      const candidates = nav.candidates.map((candidate) => [
        candidate.routineKind, candidate.objectId ?? null, ...nameKey(candidate.binding.target),
      ]);
      return JSON.stringify([nav.via, c.connectionId, c.engineId, c.database ?? null, candidates]);
    }
  }
}

/** One-line description shown in the Ctrl/Cmd+hover card and the peek row. A
 *  SQL comment, so the hover's SQL code block renders it as one quiet line. */
export function describeDefinitionNavTarget(nav: DefinitionNavTarget): string {
  switch (nav.via) {
    case 'object-explorer': {
      const kind = nav.target.objectKind === 'view' ? 'View' : 'Table';
      return `-- ${kind} ${displayName(nav.target.qualified)}: reveal in Object Explorer`;
    }
    case 'column-target': {
      const column = `${displayName(nav.target.relation)}.${oneLine(nav.target.column.name)}`;
      return nav.target.relationKind === 'view'
        ? `-- Column ${column}: open the view's CREATE script in a new tab`
        : `-- Column ${column}: open in Table Designer`;
    }
    case 'routine-target': {
      const kind = nav.target.routineKind === 'function' ? 'Function' : 'Procedure';
      return `-- ${kind} ${displayName(nav.target.qualified)}: open its CREATE script in a new tab`;
    }
    case 'routine-picker': {
      const first = nav.candidates[0]?.binding.target;
      const name = first ? displayName(first) : 'routine';
      return `-- ${nav.candidates.length} overloads of ${name}: pick one to open its CREATE script`;
    }
  }
}

function nameKey(name: QualifiedName): readonly (string | null)[] {
  return [name.database ?? null, name.schema ?? null, name.name, name.foldedName];
}

function labelSegments(nav: DefinitionNavTarget): readonly (string | undefined)[] {
  switch (nav.via) {
    case 'object-explorer': {
      const t = nav.target;
      return [t.connectionId, t.qualified.database ?? t.database, t.objectKind, t.qualified.schema, t.qualified.name];
    }
    case 'column-target': {
      const t = nav.target;
      const database = t.relation.database ?? t.database;
      return [t.connectionId, database, 'column', t.relation.schema, `${t.relation.name}.${t.column.name}`];
    }
    case 'routine-target': {
      const t = nav.target;
      return [t.connectionId, t.database ?? t.qualified.database, t.routineKind, t.qualified.schema, t.qualified.name];
    }
    case 'routine-picker': {
      const first = nav.candidates[0]?.binding.target;
      return [nav.context.connectionId, nav.context.database, 'overloads', first?.schema, first?.name];
    }
  }
}

function displayName(name: QualifiedName): string {
  return oneLine(name.schema ? `${name.schema}.${name.name}` : name.name);
}

/** Quoted identifiers may legally contain line breaks; keep the description
 *  on one line so the peek row and hover card stay a single line. */
function oneLine(value: string): string {
  return value.replace(/[\r\n]+/g, ' ');
}
